import { Document, Font, Text, View } from "@react-pdf/renderer";

import { Table, TR, TD, TH } from "@ag-media/react-pdf-table";
import { Quotation } from "@/v2/types";

import CairoExtraLight from "../../../assets/fonts/cairo/static/Cairo-ExtraLight.ttf";
import CairoLight from "../../../assets/fonts/cairo/static/Cairo-Light.ttf";
import CairoRegular from "../../../assets/fonts/cairo/static/Cairo-Regular.ttf";
import CairoMedium from "../../../assets/fonts/cairo/static/Cairo-Medium.ttf";
import CairoSemiBold from "../../../assets/fonts/cairo/static/Cairo-SemiBold.ttf";
import CairoBold from "../../../assets/fonts/cairo/static/Cairo-Bold.ttf";
import CairoExtraBold from "../../../assets/fonts/cairo/static/Cairo-ExtraBold.ttf";
import CairoBlack from "../../../assets/fonts/cairo/static/Cairo-Black.ttf";
import { formatDate, normalizeArabic } from "@/v2/utils";
import PrintNewPage from "../../print/common/NewPage";
import PrintTitle from "../../print/common/Title";
import BlockRowContent from "../../print/block/base/RowContent";
import BankDetailsBlock from "../../print/block/presets/BankDetails";
import { color, textStyles } from "../../print/common";
import TermsAndConditionsBlock from "../../print/block/presets/TermsAndConditions";
import FromBlock from "../../print/block/presets/From";
import ToBlock from "../../print/block/presets/To";
import PrintBlock from "../../print/block/base/Block";
import GrandTotalBlock from "../../print/block/presets/GrandTotal";
import FinalAmountDetailsBlock from "../../print/block/presets/FinalAmountDetails";
import TotalAmountInWords from "../../print/block/presets/TotalAmountInWords";
import BlockHeading from "../../print/block/base/Heading";
import { Branches } from "@/v2/constants";

Font.registerHyphenationCallback((word) => [word]);

Font.register({
    family: "ArabicFont",
    fonts: [
        { src: CairoExtraLight, fontWeight: 100 },
        { src: CairoExtraLight, fontWeight: 200 },
        { src: CairoLight, fontWeight: 300 },
        { src: CairoRegular, fontWeight: 400 },
        { src: CairoMedium, fontWeight: 500 },
        { src: CairoSemiBold, fontWeight: 600 },
        { src: CairoBold, fontWeight: 700 },
        { src: CairoExtraBold, fontWeight: 800 },
        { src: CairoBlack, fontWeight: 900 },
    ],
});

const TableTitle = ({
    arabicTitle,
    englishTitle,
}: {
    arabicTitle: string;
    englishTitle: string;
}) => (
    <View>
        <Text style={textStyles.arabicText}>
            {normalizeArabic(arabicTitle)}
        </Text>
        <Text>{englishTitle}</Text>
    </View>
);

const QuotationPdf = ({
    data,
    addressBranch,
}: {
    data: Quotation;
    addressBranch: Branches;
}) => {
    // Calculate totals
    const calculateTotals = () => {
        if (!data.items || data.items.length === 0) {
            return { totalTaxable: 0, totalTax: 0, grandTotal: 0 };
        }

        let totalTaxable = 0;
        let totalTax = 0;

        data.items.forEach((item) => {
            const unitPrice = Number(item.unit_price) || 0;
            const quantity = Number(item.quantity) || 0;
            const taxRate = Number(item.tax) || 0;

            const itemTaxable = unitPrice * quantity;
            const itemTax = itemTaxable * (taxRate / 100);

            totalTaxable += itemTaxable;
            totalTax += itemTax;
        });

        const grandTotal = totalTaxable + totalTax;

        return {
            totalTaxable: totalTaxable.toFixed(2),
            totalTax: totalTax.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
        };
    };

    const { totalTaxable, totalTax, grandTotal } = calculateTotals();

    return (
        <Document>
            <PrintNewPage>
                <PrintTitle
                    englishTitle="QUOTATION"
                    arabicTitle="عرض الأسعار"
                />

                <View
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 10,
                    }}
                >
                    <FromBlock
                        fromHeading="Quote From"
                        formHeadingArabic="عرض السعر من"
                        addressBranch={addressBranch}
                    />

                    <ToBlock
                        full_name={data.client?.full_name}
                        full_name_ar={data.client?.full_name_ar}
                        company_address={data.client?.company_address}
                        address_ar={data.client?.address_ar}
                        vat_number={data.client?.vat_number}
                        email={data.client?.email}
                        cr_number={data.client?.cr_number}
                    />
                </View>

                <View
                    style={{
                        backgroundColor: color.bg,
                        marginVertical: "4px",
                        display: "flex",
                        flexDirection: "row",
                        marginTop: 10,
                        paddingVertical: "10px",
                        borderRadius: "4px",
                    }}
                >
                    <PrintBlock
                        style={{
                            flex: "1",
                            paddingHorizontal: "10px",
                        }}
                    >
                        <BlockRowContent
                            englishLabel="Quotation No."
                            arabicLabel="رقم عرض السعر"
                            content={data.quotation_number || "N/A"}
                            disableArabicContent
                            boldContent={true}
                        />
                        <BlockRowContent
                            englishLabel="Quotation Date"
                            arabicLabel="تاريخ عرض السعر"
                            content={formatDate(data.quotation_date) || "N/A"}
                            disableArabicContent
                        />
                        <BlockRowContent
                            englishLabel="Created By"
                            arabicLabel="تم الإنشاء بواسطة"
                            content={data.user?.full_name || "N/A"}
                            disableArabicContent
                        />
                    </PrintBlock>

                    <PrintBlock
                        style={{
                            flex: "1",
                            paddingLeft: "14px",
                            paddingRight: "10px",
                        }}
                    >
                        <BlockRowContent
                            englishLabel="Reference No."
                            arabicLabel="رقم المرجع"
                            content={data.reference_number || "N/A"}
                            disableArabicContent
                        />
                    </PrintBlock>
                </View>

                <View
                    style={{
                        marginVertical: "20px",
                        textAlign: "justify",
                        fontSize: "9px",
                    }}
                >
                    <View style={{ display: "flex", flexDirection: "row" }}>
                        <Text>Attention : </Text>
                        <View>
                            <Text>{data.recipient_name || "N/A"}</Text>
                            <Text
                                style={{
                                    color: "#7d7d7d",
                                    fontStyle: "italic",
                                }}
                            >
                                {data.recipient_designation || ""}
                            </Text>
                        </View>
                    </View>

                    <View
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 4,
                            fontWeight: 600,
                            marginVertical: "10px",
                        }}
                    >
                        <Text>Subject : </Text>
                        <Text style={{ fontStyle: "italic" }}>
                            {data.subject || "N/A"}
                        </Text>
                    </View>

                    <View
                        style={{
                            textAlign: "justify",
                            display: "flex",
                            flexDirection: "column",
                            gap: "5px",
                        }}
                    >
                        <View>
                            <Text style={{ fontWeight: 200 }}>
                                Thank you for the opportunity to conduct
                                business with{" "}
                                <Text style={{ fontWeight: 600 }}>
                                    Abdul Lathif Mohammed Al Rifae Est.{" "}
                                </Text>
                                We have analyzed your requirements and are
                                pleased to submit our Proposal for subcontract
                                work.
                            </Text>
                        </View>
                        <View>
                            <Text>
                                Attached, please find a copy of our service
                                proposal Recommendation for your consideration.
                                I am sure that you will find our service
                                proposal to be beneficial to your esteemed
                                company,
                            </Text>
                        </View>
                        <View style={{ margin: "10px" }}>
                            <Text>
                                This offer is especially tailored to{" "}
                                <Text style={{ fontWeight: 600 }}>
                                    {data.client?.full_name || "your company"},
                                </Text>
                                Requirements and characteristics which desire a
                                high-quality service at an affordable price to
                                develop its services with a bit of professional
                                assistance to kick start your venture. With our
                                proposal adopted{" "}
                                <Text style={{ fontWeight: 600 }}>
                                    {data.client?.full_name || "your company"},
                                </Text>
                                will experience progressive developments in its
                                business.
                            </Text>
                        </View>
                        <View>
                            <Text>
                                Please feel free to contact me with any
                                questions that you may have regarding the
                                proposal. Your cooperation is appreciated.
                            </Text>
                        </View>
                    </View>
                </View>
                <Table
                    style={{
                        border: "0px",
                        borderRadius: "4px",
                        overflow: "hidden",
                    }}
                    weightings={[0.125, 1, 0.5, 0.3, 0.5, 0.75, 0.7, 0.375]}
                    trStyle={{
                        backgroundColor: color.bg,
                        paddingHorizontal: "4px",
                    }}
                    tdStyle={{
                        padding: "2px",
                    }}
                    wrap={false}
                >
                    <TH
                        style={{
                            backgroundColor: color.brand,
                            color: "#ffffff",
                            padding: "4px",
                        }}
                    >
                        <TD>No.</TD>
                        <TD>
                            <TableTitle
                                arabicTitle="تفاصيل السلع أو الخدمات"
                                englishTitle={`NATURE OF GOODS OR${"\n"}SERVICES`}
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="سعر الوحدة"
                                englishTitle="UNIT PRICE"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="كمية"
                                englishTitle="QUANTITY"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="وحدة قياس"
                                englishTitle="UNIT"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="المبلغ الخاضع للضريبة"
                                englishTitle="TAXABLE AMOUNT"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="الضريبة@ نسبة الضريبة"
                                englishTitle="TAX @TAX RATE"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="المجموع"
                                englishTitle="TOTAL"
                            />
                        </TD>
                    </TH>

                    {data.items && data.items.length > 0 ? (
                        data.items.map((item, index) => {
                            const unitPrice = Number(item.unit_price) || 0;
                            const quantity = Number(item.quantity) || 0;
                            const taxRate = Number(item.tax) || 0;

                            const taxableAmount = unitPrice * quantity;
                            const taxAmount = taxableAmount * (taxRate / 100);
                            const totalAmount = taxableAmount + taxAmount;

                            return (
                                <TR
                                    style={{
                                        paddingVertical: "10px",
                                    }}
                                    key={index}
                                    wrap={false}
                                >
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {index + 1}
                                    </TD>

                                    <TD
                                        style={{
                                            display: "flex",
                                        }}
                                    >
                                        {item.description}
                                    </TD>

                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.unit_price}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.quantity}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.uom}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {taxableAmount.toFixed(2)}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {taxAmount.toFixed(2)}@{taxRate}%
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {totalAmount.toFixed(2)}
                                    </TD>
                                </TR>
                            );
                        })
                    ) : (
                        <TR>
                            <TD
                                style={{ textAlign: "center", padding: "10px" }}
                            >
                                No items available
                            </TD>
                        </TR>
                    )}
                </Table>

                <View style={{ marginTop: "10px" }}>
                    <View
                        wrap={false}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "20px",
                        }}
                    >
                        <View style={{ flex: "1", width: "50%" }}>
                            <BankDetailsBlock />
                            <TermsAndConditionsBlock
                                terms={data.terms_and_conditions || []}
                            />
                        </View>

                        <View
                            wrap={false}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                width: "50%",
                            }}
                        >
                            <PrintBlock>
                                <BlockHeading
                                    englishTitle="Financial Summary"
                                    arabicTitle="الملخص المالي"
                                />
                            </PrintBlock>

                            <FinalAmountDetailsBlock
                                englishLabel="Total Taxable Amount"
                                arabicLabel="إجمالي المبلغ الخاضع للضريبة"
                                amount={totalTaxable}
                            />
                            <FinalAmountDetailsBlock
                                englishLabel="Total Tax Amount"
                                arabicLabel="إجمالي مبلغ الضريبة"
                                amount={totalTax}
                            />
                            <GrandTotalBlock
                                englishLabel="Grand Total"
                                arabicLabel="الإجمالي الكلي"
                                amount={grandTotal}
                            />

                            <View
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <TotalAmountInWords
                                    grandTotal={Number(grandTotal)}
                                />
                            </View>
                        </View>
                    </View>
                </View>
            </PrintNewPage>
        </Document>
    );
};

export default QuotationPdf;
